import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, FormFeedback, Input, Label, Form } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import {
  applyLoan,
  fetchCodeList,
  fetchLoanById,
  fetchOutstandingLoanData,
} from '../../../store/apis/loanService';
import {
  employeePayload,
  initialValues,
  validationSchema,
  populateLoanValues,
} from './constants';
import {
  ErrorModalWrapper,
  ErrorTable,
  DatePicker,
} from '../../../components/Common';
import OutstandingTable from '../../../components/Loan/GetOutstandingLoanModal/OutstandingTable';
import { getAuthUser } from '../../../utils/auth.util';
import '../index.scss';

export const installmentOptions = {
  '01': [1, 2, 3, 4, 5, 6],
  '02': [1],
  '04': [1, 2, 3, 4, 5, 6, 12, 24, 36, 48, 60],
};

function ApplyLoan() {
  document.title = 'Apply Loan | Envision - Daikin';
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { ID } = Object.fromEntries(query);
  const formType = ID ? 'view' : 'apply';

  const [errorModal, setErrorModal] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [outstandingLoans, setOutstandingLoans] = useState([]);
  const [cannotApplyError, setCannotApplyError] = useState(false);
  const errorBannerRef = useRef(null);
  const hrCoCd = getAuthUser()?.hrCoCd;

  const toggleErrorModal = () => setErrorModal(!errorModal);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
    setValue,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  const values = watch();

  const { mutate: applyLoanMutation, isPending: applyLoanPending } =
    useMutation({
      mutationFn: payload => applyLoan(payload),
      onSuccess: data => {
        if (data.flag === 'True') {
          toast.success(
            data?.message || 'Loan application submitted successfully',
            {
              className: 'toast-success',
            },
          );
          reset();
        } else if (data.flag === 'False') {
          if (
            data?.message ===
            'Loan cannot be applied without completely repaying the existing loan'
          ) {
            setCannotApplyError(true);
          } else {
            data.isTable = false;
            setErrorModal(true);
            setErrorData(data);
          }
        }
      },
    });

  const { data: currencies } = useQuery({
    queryKey: ['getCurrencies'],
    queryFn: () =>
      fetchCodeList({
        ...employeePayload,
        codeType: 'CURRENCY',
        param1: '',
        param2: '',
        param3: '',
      }),
  });

  const { data: loanTypes } = useQuery({
    queryKey: ['getLoanTypes'],
    queryFn: () =>
      fetchCodeList({
        ...employeePayload,
        codeType: 'LOAN_TYPE',
        param1: '',
        param2: '',
        param3: '',
      }),
  });

  const { data: payModes } = useQuery({
    queryKey: ['getPayModes'],
    queryFn: () =>
      fetchCodeList({
        ...employeePayload,
        codeType: 'PAY_MODE',
        param1: '',
        param2: '',
        param3: '',
      }),
  });

  const { data: loanData } = useQuery({
    queryKey: ['loanData', ID],
    queryFn: () => fetchLoanById({ loanId: ID }),
    enabled: formType === 'view',
  });

  const { data: outstandingLoansData, isLoading: isOutstandingLoading } =
    useQuery({
      queryKey: ['getOutstandingLoans'],
      queryFn: () => fetchOutstandingLoanData(),
      enabled: true,
    });

  useEffect(() => {
    if (formType === 'view' && loanData && loanData.length > 0) {
      const editFormValues = populateLoanValues(
        loanData[0],
        loanTypes,
        payModes,
      );
      reset(editFormValues);
    }
  }, [formType, loanData, reset, loanTypes, payModes]);

  useEffect(() => {
    if (outstandingLoansData && Array.isArray(outstandingLoansData.result)) {
      setOutstandingLoans(outstandingLoansData.result);
    }
  }, [outstandingLoansData]);

  const formatDate = input => {
    const date = new Date(input);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const calculateInstallmentAmount = React.useCallback(() => {
    const loanAmount = parseFloat(values.loanAmt);
    const numberOfInstallments = parseInt(values.loanInst, 10);
    if (loanAmount && numberOfInstallments) {
      const installmentAmount = loanAmount / numberOfInstallments;
      return installmentAmount.toFixed(2);
    }
    return 0;
  }, [values.loanAmt, values.loanInst]);

  const onSubmit = () => {
    setCannotApplyError(false);
    const formData = {
      ...values,
      loanPayDate: formatDate(values.loanPayDate),
      ...employeePayload,
    };
    applyLoanMutation(formData);
  };

  useEffect(() => {
    if (calculateInstallmentAmount() > 0) {
      setValue('loanInstAmt', calculateInstallmentAmount());
    }
  }, [values.loanAmt, values.loanInst, calculateInstallmentAmount, setValue]);

  useEffect(() => {
    if (cannotApplyError && errorBannerRef.current) {
      errorBannerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [cannotApplyError]);

  useEffect(() => {
    if (errorData && !errorData?.isTable) {
      toast.error(errorData?.message || 'An error occurred', {
        className: 'toast-error',
      });
    }
  }, [errorData]);

  return (
    <div className="page-content px-0 apply-forms">
      <div className="d-flex flex-row p-0">
        <div className="col-7 p-0 h-100">
          {/* Header */}
          <div className="d-flex flex-row align-items-center justify-content-between leave-layout-border-bottom">
            {formType !== 'view' ? (
              <div className="ps-3 py-3 d-flex align-items-center fs-5 fw-semibold apply-header">
                Apply Loan
              </div>
            ) : (
              <div className="ps-1 py-3 d-flex flex-row align-items-center gap-2 apply-header">
                <button
                  type="button"
                  className="btn btn-link p-0 back-btn"
                  onClick={() => window.history.back()}
                >
                  <Icon path={mdiChevronLeft} size={1.1} />
                  <span className="back fs-5">Back</span>
                </button>
                <div
                  style={{
                    width: '1px',
                    borderLeft: '1px solid lightgrey',
                    height: '20px',
                  }}
                />
                <div className="d-flex flex-row gap-1">
                  <span className="request-id-span fs-5">Request ID</span>
                  <span className="request-id-span-value fs-5">{ID}</span>
                </div>
              </div>
            )}
          </div>
          {cannotApplyError && (
            <div
              className="d-flex align-items-center justify-content-center p-3 error-banner"
              ref={errorBannerRef}
            >
              <span>
                Loan cannot be applied without completely repaying the existing
                loan
              </span>
            </div>
          )}

          {/* Form */}
          <Form
            className="d-flex flex-column gap-2 p-3"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Loan Type */}
            <Col>
              <Label className="my-1">
                Loan Type<span className="text-danger">*</span>
              </Label>
              <Controller
                name="loanType"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="select"
                    disabled={formType === 'view'}
                    className={`external-filter-input ${errors.loanType ? 'is-invalid' : ''}`}
                    title="Select the type of loan you are applying for from the available dropdown options."
                  >
                    <option value="">Select Loan Type</option>
                    {loanTypes?.map(item => (
                      <option key={item.Code} value={item.Code}>
                        {item.Description}
                      </option>
                    ))}
                    {formType === 'view' && (
                      <option value="RECOVERY">RECOVERY</option>
                    )}
                  </Input>
                )}
              />
              {errors.loanType && (
                <FormFeedback>{errors.loanType.message}</FormFeedback>
              )}
            </Col>
            {/* Purpose */}
            <Col>
              <Label className="my-1">
                Purpose<span className="text-danger">*</span>
              </Label>
              <Controller
                name="loanPurpose"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    placeholder="Enter Purpose"
                    className={`external-filter-input ${errors.loanPurpose ? 'is-invalid' : ''}`}
                    disabled={formType === 'view'}
                    title="Enter the purpose or reason for requesting the loan."
                  />
                )}
              />
              {errors.loanPurpose && (
                <FormFeedback>{errors.loanPurpose.message}</FormFeedback>
              )}
            </Col>

            {/* Currency and Loan Amount */}
            <div className="d-flex flex-row justify-content-between align-items-start gap-2">
              <Col lg={3}>
                <Label className="my-1">
                  Currency<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`external-filter-input ${errors.currency ? 'is-invalid' : ''}`}
                      disabled={formType === 'view'}
                      title="Choose a currency for the loan amount from the available dropdown options"
                    >
                      <option value="">Select Currency</option>
                      {currencies?.map(item => (
                        <option key={item.Code} value={item.Code}>
                          {item.Description}
                        </option>
                      ))}
                    </Input>
                  )}
                />
                {errors.currency && (
                  <FormFeedback>{errors.currency.message}</FormFeedback>
                )}
              </Col>

              <Col lg={9}>
                <Label className="my-1">
                  Loan Amount<span className="text-danger">*</span>
                </Label>
                <Controller
                  name="loanAmt"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      placeholder="0"
                      className={`external-filter-input ${errors.loanAmt ? 'is-invalid' : ''}`}
                      disabled={formType === 'view'}
                      title="Enter the loan amount you wish to apply for"
                    />
                  )}
                />
                {errors.loanAmt && (
                  <FormFeedback>{errors.loanAmt.message}</FormFeedback>
                )}
              </Col>
            </div>

            {/* Installments */}
            <Col>
              <Label className="my-1">
                No. of Installments<span className="text-danger">*</span>
              </Label>
              <Controller
                name="loanInst"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="select"
                    className={`external-filter-input ${errors.loanInst ? 'is-invalid' : ''}`}
                    disabled={formType === 'view'}
                    title="Enter number of installments for loan repayment"
                  >
                    <option value="">Select No. of Installments</option>
                    {installmentOptions[values.loanType]?.map(item => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </Input>
                )}
              />
              {errors.loanInst && (
                <FormFeedback>{errors.loanInst.message}</FormFeedback>
              )}
            </Col>

            {/* Installment Message */}
            {calculateInstallmentAmount() > 0 && (
              <div
                style={{
                  backgroundColor: '#FFFCF5',
                  border: '1px solid #FEC84B',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: '#B54708',
                  fontWeight: '600',
                }}
              >
                Monthly installment amount {calculateInstallmentAmount()} AED
                will be deducted.
              </div>
            )}

            {/* Hidden Installment Amount Field */}
            <Col style={{ display: 'none' }}>
              <Label className="my-1">Installment Amount</Label>
              <Controller
                name="loanInstAmt"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    readOnly
                    title="Amount per installment"
                  />
                )}
              />
            </Col>

            {/* Pay With Salary Of */}
            <Col>
              <Label className="my-1">
                Pay with salary of<span className="text-danger">*</span>
              </Label>
              <Controller
                name="loanPayDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    disabled={formType === 'view'}
                    className={`external-filter-input ${errors.loanPayDate ? 'is-invalid' : ''}`}
                    error={!!errors.loanPayDate}
                    type="month"
                    title="Select the salary month and year from which the loan repayment should start."
                    defaultToNextMonth
                  />
                )}
              />
              {errors.loanPayDate && (
                <div className="invalid-feedback d-block">
                  {errors.loanPayDate.message}
                </div>
              )}
            </Col>

            {/* Pay Mode */}
            <Col>
              <Label className="my-1">
                Desired Pay Mode<span className="text-danger">*</span>
              </Label>
              <Controller
                name="payMode"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="select"
                    className={`external-filter-input ${errors.payMode ? 'is-invalid' : ''}`}
                    disabled={formType === 'view'}
                    title="Select payment mode from available dropdown options"
                  >
                    <option value="">Select Pay Mode</option>
                    {payModes?.map(item => (
                      <option key={item.Code} value={item.Code}>
                        {item.Description}
                      </option>
                    ))}
                    {formType === 'view' && (
                      <option value="Cheque">Cheque</option>
                    )}
                  </Input>
                )}
              />
              {errors.payMode && (
                <FormFeedback>{errors.payMode.message}</FormFeedback>
              )}
            </Col>

            {/* Comments */}
            <Col>
              <Label className="my-1">Comments</Label>
              <Controller
                name="comments"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="textarea"
                    placeholder="Enter Comments"
                    className="external-filter-input"
                    disabled={formType === 'view'}
                    title="Add any additional information or notes related to your loan request."
                  />
                )}
              />
            </Col>

            {/* Buttons */}
            {formType === 'apply' && (
              <div className="d-flex flex-row align-items-center justify-content-center gap-2 p-3">
                <Button
                  className="custom-light-btn"
                  style={{
                    width: '25%',
                  }}
                  onClick={() => reset()}
                >
                  Clear
                </Button>
                <Button
                  className="custom-btn"
                  style={{
                    width: '25%',
                  }}
                  disabled={!isValid || applyLoanPending}
                  type="submit"
                >
                  {applyLoanPending ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            )}

            {/* Outstanding Loans (for both view and apply modes) */}
            {Array.isArray(outstandingLoans) && outstandingLoans.length > 0 && (
              <Col>
                <div className="pb-4">
                  <h5 className="fw-bold">Outstanding Loan</h5>
                  <OutstandingTable
                    outstanding={outstandingLoans}
                    isLoading={isOutstandingLoading}
                  />
                </div>
              </Col>
            )}
          </Form>
        </div>

        {/* Terms & Conditions Column */}
        <div
          className="col-5 leave-layout-border-left"
          style={{
            color: 'black',
          }}
        >
          <div className="tnc-wrapper px-3 pt-3">
            <b className="tnc-title mb-1">Terms & Conditions</b>
            {['01', '02', '03'].includes(hrCoCd) && (
              <>
                {' '}
                <b className="mb-2">Housing Advance</b>
                <ol>
                  <li>
                    Maximum housing advance that can be requested is equivalent
                    to 6 months Housing Allowance.
                  </li>
                  <li>Installment period cannot be more than 6 months.</li>
                  {['01', '03'].includes(hrCoCd) && (
                    <li>
                      Deduction starts from the following month on which loan
                      paid.
                    </li>
                  )}
                  {['02'].includes(hrCoCd) && (
                    <li>
                      Deduction starts from the same month on which advance
                      paid.
                    </li>
                  )}
                </ol>
                <b className="mb-2">Salary Advance</b>
                <ol>
                  <li>
                    Maximum salary advance that can be requested is equivalent
                    to 1 month salary.
                  </li>
                  <li>Installment period cannot be more than 1 month.</li>
                  <li>
                    Deduction starts from the following month on which loan
                    paid.
                  </li>
                </ol>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Error Modal */}
      {errorData && errorData?.isTable && (
        <ErrorModalWrapper
          isModalOpen={errorModal}
          toggle={toggleErrorModal}
          errorMessage={errorData?.message}
          onButtonClick={toggleErrorModal}
          size="xl"
        >
          {errorData?.isTable && (
            <ErrorTable errorData={errorData?.errorData} />
          )}
        </ErrorModalWrapper>
      )}
    </div>
  );
}

export default ApplyLoan;
